import { formatReportPeriodRange, REPORT_PERIOD_OPTIONS } from '@repo/shared/reports';
import type {
  CommentDmNumbers,
  GetMarketingCommentAutomations200Data,
  GetMarketingCommentAutomations200DataItemsItem,
  GetMarketingCommentAutomationsPeriod,
} from '@/api/generated/schemas';
import { formatCount, formatUsd } from '../analytics/analytics-format';
import { percentOf } from './comment-dm-format';

/**
 * Words and shapes for the Comment → DM results page. Every column, tile and definition follows the `setup` the payload
 * carries (the salon as the API judged the numbers): Booked only where bookings are in Hayven, money only where Hayven
 * values a visit, "at least" where only the counter proves a visit. Never a second settings read.
 */

export type CommentDmPeriod = GetMarketingCommentAutomationsPeriod;
export type CommentDmResultsData = GetMarketingCommentAutomations200Data;
export type CommentDmResultsMeta = CommentDmResultsData['meta'];
export type CommentDmPostRow = GetMarketingCommentAutomations200DataItemsItem;

export const DEFAULT_COMMENT_DM_PERIOD: CommentDmPeriod = 'this-month';

/** Reports periods plus "All time" (the API takes both). */
export const COMMENT_DM_PERIOD_OPTIONS: ReadonlyArray<{ value: CommentDmPeriod; label: string }> = [
  ...REPORT_PERIOD_OPTIONS,
  { value: 'all-time', label: 'All time' },
];

/** `?period=` → a period the API takes; anything else (missing, stale, typo) reads as the default. */
export function parseCommentDmPeriod(raw: unknown): CommentDmPeriod {
  return COMMENT_DM_PERIOD_OPTIONS.some((option) => option.value === raw) ? (raw as CommentDmPeriod) : DEFAULT_COMMENT_DM_PERIOD;
}

const PERIOD_PHRASE: Record<CommentDmPeriod, string> = {
  'this-month': 'this month yet',
  'last-month': 'last month',
  'last-7-days': 'in the last 7 days',
  'last-30-days': 'in the last 30 days',
  'last-90-days': 'in the last 90 days',
  'this-year': 'this year yet',
  'all-time': 'yet',
};

/** The period inside the empty line: "No keyword comments this month yet" / "… last month" / "… in the last 7 days". */
export function commentDmPeriodPhrase(period: CommentDmPeriod): string {
  return PERIOD_PHRASE[period];
}

/** "Oct 1–27" · "Since Oct 4, 2026" (all time, from the first comment) · "All time" (no comment ever). */
export function commentDmPeriodRange(meta: CommentDmResultsMeta): string {
  if (meta.fromIso) return formatReportPeriodRange({ fromIso: meta.fromIso, toIso: meta.toIso });
  if (!meta.firstCommentAt) return 'All time';
  const since = new Date(meta.firstCommentAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: meta.timezone,
  });
  return `Since ${since}`;
}

export type CommentDmColumnKey = 'comments' | 'dmsSent' | 'tapped' | 'newSignups' | 'booked' | 'cameIn' | 'earned';
export type CommentDmTone = 'plain' | 'brand' | 'success';

export interface CommentDmColumn {
  key: CommentDmColumnKey;
  label: string;
  tone: CommentDmTone;
}

/** Booked shows only where the salon's bookings live in Hayven. */
export const showsBooked = (meta: CommentDmResultsMeta) => meta.setup.bookingsInHayven;
/** Money shows only where Hayven values a visit (real sales or an estimate). */
export const showsMoney = (meta: CommentDmResultsMeta) => meta.setup.money !== 'NONE';

/** "Earned" for real sales, "Earned (est.)" for a menu or Turns estimate (posts table, Results card, its ⓘ). */
export const commentDmEarnedLabel = (meta: CommentDmResultsMeta) => (meta.setup.money === 'SALES' ? 'Earned' : 'Earned (est.)');

/** The posts table's number columns for this setup, in funnel order. */
export function commentDmColumns(meta: CommentDmResultsMeta): CommentDmColumn[] {
  const columns: CommentDmColumn[] = [
    { key: 'comments', label: 'Comments', tone: 'plain' },
    { key: 'dmsSent', label: 'DMs sent', tone: 'plain' },
    { key: 'tapped', label: 'Tapped', tone: 'plain' },
    { key: 'newSignups', label: 'New sign-ups', tone: 'brand' },
  ];
  if (showsBooked(meta)) columns.push({ key: 'booked', label: 'Booked', tone: 'plain' });
  columns.push({ key: 'cameIn', label: meta.cameInAtLeast ? 'Came in (at least)' : 'Came in', tone: 'success' });
  if (showsMoney(meta)) columns.push({ key: 'earned', label: commentDmEarnedLabel(meta), tone: 'plain' });
  return columns;
}

/**
 * One cell: a count, whole dollars for money, "—" where the number is unknown. A post row reads "—" for no money too
 * (`zeroMoneyAsDash`), as the mockup does; tiles and the totals row keep "$0".
 */
export function formatCommentDmCell(numbers: CommentDmNumbers, key: CommentDmColumnKey, options: { zeroMoneyAsDash?: boolean } = {}): string {
  if (key === 'earned') {
    if (numbers.earnedCents === null || (options.zeroMoneyAsDash && numbers.earnedCents === 0)) return '—';
    return formatUsd(numbers.earnedCents);
  }
  const value = numbers[key];
  return value === null ? '—' : formatCount(value);
}

/** A cell's raw number (cents for money), null where the setup can't know it: how the cell is colored, how rows add up. */
export function commentDmCellValue(numbers: CommentDmNumbers, key: CommentDmColumnKey): number | null {
  return key === 'earned' ? numbers.earnedCents : numbers[key];
}

const MONEY_SUB: Record<CommentDmResultsMeta['setup']['money'], string | null> = {
  SALES: 'Paid in Hayven',
  MENU_ESTIMATE: 'Est. from menu prices',
  TURNS_ESTIMATE: 'Est. from finished turns',
  NONE: null,
};

/** Where the money comes from, as a short sub line ("Paid in Hayven" / "Est. from menu prices"); null without money. */
export function commentDmMoneySub(meta: CommentDmResultsMeta): string | null {
  return MONEY_SUB[meta.setup.money];
}

const ofLine = (part: number, whole: number, of: string) => {
  const rate = percentOf(part, whole);
  return rate === null ? `No ${of} yet` : `${rate}% of ${of}`;
};

export interface CommentDmTile {
  key: CommentDmColumnKey;
  label: string;
  value: string;
  sub: string;
  tone: CommentDmTone;
}

/** The post drawer's tiles: the funnel for the period, each with how it compares to the step before. */
export function commentDmResultTiles(numbers: CommentDmNumbers, meta: CommentDmResultsMeta, posts: number): CommentDmTile[] {
  const tile = (key: CommentDmColumnKey, label: string, sub: string, tone: CommentDmTone = 'plain'): CommentDmTile => ({
    key,
    label,
    value: formatCommentDmCell(numbers, key),
    sub,
    tone,
  });
  const tiles = [
    tile('comments', 'Comments', `on ${formatCount(posts)} ${posts === 1 ? 'post' : 'posts'}`),
    tile('dmsSent', 'DMs sent', ofLine(numbers.dmsSent, numbers.comments, 'comments')),
    tile('tapped', 'Tapped', ofLine(numbers.tapped, numbers.dmsSent, 'DMs')),
    tile('newSignups', 'New sign-ups', ofLine(numbers.newSignups, numbers.tapped, 'taps'), 'brand'),
  ];
  if (showsBooked(meta)) tiles.push(tile('booked', 'Booked', ofLine(numbers.booked ?? 0, numbers.newSignups, 'sign-ups')));
  const cameIn = ofLine(numbers.cameIn, numbers.newSignups, 'sign-ups');
  const atLeast = `At least · ${cameIn.charAt(0).toLowerCase()}${cameIn.slice(1)}`;
  tiles.push(tile('cameIn', 'Came in', meta.cameInAtLeast ? atLeast : cameIn, 'success'));
  const moneySub = commentDmMoneySub(meta);
  if (moneySub) tiles.push(tile('earned', 'Earned', moneySub));
  return tiles;
}

const POST_KIND_LABEL = { VIDEO: 'Video', CAROUSEL: 'Carousel', PHOTO: 'Photo' } as const;

/**
 * "Instagram · Video · Oct 18" (kind left out while unknown, date while the post has none); `posted` reads the date as
 * "Posted Oct 18" (the post drawer).
 */
export function postMetaLine(
  post: CommentDmPostRow['post'],
  platformLabel: string,
  timezone?: string,
  options: { posted?: boolean } = {}
): string {
  const kind = post.mediaKind ? POST_KIND_LABEL[post.mediaKind] : null;
  const day = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: timezone })
    : null;
  const date = day && options.posted ? `Posted ${day}` : day;
  return [platformLabel, kind, date].filter(Boolean).join(' · ');
}
